/**
 * Pagination — offset-based controls for server-paginated tables.
 *
 * Phase-2 Wave-2 W2.T5 primitive. Per V2-FIX (Phase-2 PLAN §2 + research-A
 * §3 footnote), the API ships offset pagination; this component is the FE
 * mirror. Parent owns `page` (1-based), `pageSize`, `totalRows`, and
 * receives `onPageChange` / `onPageSizeChange` callbacks.
 *
 * Visible elements (research-A §3 Pagination):
 *   - "Стр. X из N (Y записей)" label.
 *   - Prev / Next buttons disabled at boundaries.
 *   - Page-size select (default options 25 / 50 / 100; overridable).
 *
 * A11y:
 *   - The wrapper is a `<nav aria-label="…">`; SR users hear it as a
 *     navigation landmark.
 *   - Disabled prev/next buttons stay focusable for assistive-tech parity
 *     (we set `aria-disabled` + `disabled` so click is a no-op but the
 *     button is still in the tab order — matches browser native pagination
 *     idioms).
 *   - The "Стр. X из N" label is a polite live region so screen-reader
 *     users hear page changes without losing focus.
 */
import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

const DEFAULT_PAGE_SIZES = [25, 50, 100] as const;

export interface PaginationProps {
  /** 1-based current page. */
  page: number;
  /** Page size in rows. */
  pageSize: number;
  /** Total row count from the server. */
  totalRows: number;
  /** Page change handler — receives the new 1-based page index. */
  onPageChange: (next: number) => void;
  /** Optional page-size change handler. When omitted, the size selector is hidden. */
  onPageSizeChange?: (next: number) => void;
  /** Override the default 25/50/100 selector values. */
  pageSizes?: readonly number[];
  /** Extra class on the outer nav. */
  className?: string;
  /** ARIA label for the nav landmark. */
  ariaLabel?: string;
}

function totalPagesOf(totalRows: number, pageSize: number): number {
  if (pageSize <= 0 || totalRows <= 0) return 0;
  return Math.ceil(totalRows / pageSize);
}

export function Pagination({
  page,
  pageSize,
  totalRows,
  onPageChange,
  onPageSizeChange,
  pageSizes = DEFAULT_PAGE_SIZES,
  className,
  ariaLabel = 'Постраничная навигация',
}: PaginationProps): React.ReactElement {
  const totalPages = totalPagesOf(totalRows, pageSize);
  // Clamp current page to a valid 1..totalPages range so a stale `page=5`
  // after a row-count drop doesn't render a phantom "Стр. 5 из 2".
  const clampedPage = totalPages === 0 ? 0 : Math.min(Math.max(1, page), totalPages);
  const isFirst = clampedPage <= 1;
  const isLast = clampedPage >= totalPages || totalPages === 0;

  const handlePrev = React.useCallback(() => {
    if (!isFirst) onPageChange(clampedPage - 1);
  }, [isFirst, clampedPage, onPageChange]);

  const handleNext = React.useCallback(() => {
    if (!isLast) onPageChange(clampedPage + 1);
  }, [isLast, clampedPage, onPageChange]);

  const handleSizeChange = React.useCallback(
    (raw: string) => {
      if (!onPageSizeChange) return;
      const next = Number(raw);
      if (Number.isFinite(next) && next > 0) onPageSizeChange(next);
    },
    [onPageSizeChange],
  );

  return (
    <nav
      aria-label={ariaLabel}
      className={cn(
        'flex flex-col items-stretch gap-3 px-1 py-2 text-sm sm:flex-row sm:items-center sm:justify-between',
        className,
      )}
    >
      <p
        aria-live="polite"
        aria-atomic="true"
        className="text-muted"
        data-testid="pagination-summary"
      >
        {totalRows === 0
          ? 'Нет записей'
          : `Стр. ${clampedPage} из ${totalPages} (${totalRows} записей)`}
      </p>

      <div className="flex items-center gap-2">
        {onPageSizeChange ? (
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-muted">На странице</span>
            <Select value={String(pageSize)} onValueChange={handleSizeChange}>
              <SelectTrigger
                aria-label="Размер страницы"
                className="h-8 w-[78px]"
                data-testid="pagination-size"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {pageSizes.map((size) => (
                  <SelectItem key={size} value={String(size)}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : null}

        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={handlePrev}
            disabled={isFirst}
            aria-label="Предыдущая страница"
            data-testid="pagination-prev"
          >
            <ChevronLeft className="size-4" aria-hidden="true" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={handleNext}
            disabled={isLast}
            aria-label="Следующая страница"
            data-testid="pagination-next"
          >
            <ChevronRight className="size-4" aria-hidden="true" />
          </Button>
        </div>
      </div>
    </nav>
  );
}
